import * as React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { useFocusTrap } from '@/hooks';

interface NoFieldsDetectedModalProps {
	onConfirm: () => void;
	onCancel: () => void;
}

export const NoFieldsDetectedModal: React.FC<NoFieldsDetectedModalProps> = ({
	onConfirm,
	onCancel,
}) => {
	const dialogRef = useFocusTrap<HTMLDivElement>(true, onCancel);

	if (typeof document === 'undefined') return null;

	return createPortal(
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center p-4"
			style={{ background: 'rgba(71, 71, 71, 0.8)' }}
			onClick={onCancel}
		>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-label="No active fields detected"
				className="flex flex-col items-center bg-white"
				style={{
					width: 512,
					maxWidth: '100%',
					padding: '32px 40px',
					gap: 32,
					borderRadius: 32,
					boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.06)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-center justify-between w-full">
					<Button
						type="button"
						decoration="ghost"
						onClick={onCancel}
						aria-label="Back"
						className="!w-auto !p-0 !border-none opacity-50 hover:opacity-70 transition-opacity"
					>
						<Icon src={Icons.arrowLeft} width={24} height={24} />
					</Button>
					<div className="flex items-center gap-4">
						<Icon src={Icons.documentsEmptyState} width={61} height={48} />
						<Typography
							type="semibold-16"
							className="!text-[20px] !text-[#0D0616]"
						>
							Configure Document
						</Typography>
					</div>
					<Button
						type="button"
						decoration="ghost"
						onClick={onCancel}
						aria-label="Close"
						className="!w-auto !p-0 !border-none opacity-50 hover:opacity-70 transition-opacity"
					>
						<XMarkIcon className="size-6 text-[#0D0616]" />
					</Button>
				</div>

				<div
					className="w-full flex flex-col gap-1"
					style={{
						padding: '12px 16px',
						background: 'rgba(128, 57, 223, 0.05)',
						border: '1px solid rgba(128, 57, 223, 0.15)',
						borderRadius: 12,
					}}
				>
					<Typography
						type="semibold-12"
						className="!text-[13px] !text-[#0D0616] text-left"
					>
						No active fields detected.
					</Typography>
					<Typography
						type="regular-12"
						className="!text-[12px] !text-[#6E6A73] text-left"
					>
						This document doesn&apos;t have any fields for the client to
						complete. It will be sent for their records only—no action will be
						required on their end.
					</Typography>
				</div>

				<div className="w-full flex flex-col gap-3">
					<Button
						type="button"
						size="full"
						decoration="primary"
						label="Continue"
						className="!h-12"
						onClick={onConfirm}
					/>
					<Button
						type="button"
						size="full"
						decoration="secondary"
						label="Cancel"
						className="!h-12"
						onClick={onCancel}
					/>
				</div>
			</div>
		</div>,
		document.body
	) as React.ReactElement;
};

export default NoFieldsDetectedModal;
